import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { SCHEDULE_TAGS, scheduleApi } from 'features/Schedule/api/schedule.api'
import type {
  AvailabilityKindsResponse,
  AvailabilitySeriesResponse,
  AvailabilityWriteBody,
} from 'features/Schedule/api/availabilitySeries.types'

const headers = (siteId: string) => ({
  'Database-Connection': siteId,
  Accept: 'application/json',
})

const refreshSchedule = async (
  queryFulfilled: Promise<unknown>,
  dispatch: (action: unknown) => void,
) => {
  try {
    await queryFulfilled
    dispatch(scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]))
  } catch {
    // The modal shows the error from the mutation result.
  }
}

export const {
  useFetchAvailabilitySeriesQuery,
  useFetchAvailabilityKindsQuery,
  useCreateAvailabilitySeriesMutation,
  useUpdateAvailabilitySeriesMutation,
  useDeleteAvailabilitySeriesMutation,
  useRetryAvailabilitySeriesMutation,
  useDiscardAvailabilitySeriesMutation,
} = scheduleApi.injectEndpoints({
  endpoints: (build) => ({
    fetchAvailabilitySeries: build.query<
      AvailabilitySeriesResponse,
      { siteId: string; userId: number }
    >({
      query: ({ siteId, userId }) => ({
        url: '/availability-series',
        headers: headers(siteId),
        params: { user_id: userId, show_past: 1 },
      }),
      providesTags: [SCHEDULE_TAGS.SCHEDULE],
    }),

    fetchAvailabilityKinds: build.query<AvailabilityKindsResponse, { siteId: string }>({
      query: ({ siteId }) => ({
        url: '/availability-series/kinds',
        headers: headers(siteId),
      }),
    }),

    createAvailabilitySeries: build.mutation<
      AvailabilitySeriesResponse,
      { siteId: string; body: AvailabilityWriteBody }
    >({
      query: ({ siteId, body }) => ({
        url: '/availability-series',
        method: HTTP_METHODS.POST,
        headers: headers(siteId),
        data: body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await refreshSchedule(queryFulfilled, dispatch)
      },
    }),

    updateAvailabilitySeries: build.mutation<
      AvailabilitySeriesResponse,
      { siteId: string; id: number; body: AvailabilityWriteBody }
    >({
      query: ({ siteId, id, body }) => ({
        url: `/availability-series/${id}`,
        method: HTTP_METHODS.PUT,
        headers: headers(siteId),
        data: { ...body, scope: 'all' },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await refreshSchedule(queryFulfilled, dispatch)
      },
    }),

    deleteAvailabilitySeries: build.mutation<
      AvailabilitySeriesResponse,
      { siteId: string; id: number }
    >({
      query: ({ siteId, id }) => ({
        url: `/availability-series/${id}`,
        method: HTTP_METHODS.DELETE,
        headers: headers(siteId),
        params: { scope: 'all' },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await refreshSchedule(queryFulfilled, dispatch)
      },
    }),

    retryAvailabilitySeries: build.mutation<AvailabilitySeriesResponse, { siteId: string; id: number }>({
      query: ({ siteId, id }) => ({
        url: `/availability-series/${id}/retry`,
        method: HTTP_METHODS.POST,
        headers: headers(siteId),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await refreshSchedule(queryFulfilled, dispatch)
      },
    }),

    discardAvailabilitySeries: build.mutation<
      AvailabilitySeriesResponse,
      { siteId: string; id: number }
    >({
      query: ({ siteId, id }) => ({
        url: `/availability-series/${id}/discard`,
        method: HTTP_METHODS.POST,
        headers: headers(siteId),
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await refreshSchedule(queryFulfilled, dispatch)
      },
    }),
  }),
})
